import React, { useEffect, useRef, useState } from 'react';
import { ethers } from 'ethers';
import { useWallet } from '../../context/WalletContext';
import { useContracts } from '../../context/ContractContext';
import { useNotify } from '../../context/NotifyContext';
import Card from '../common/Card';
import Button from '../common/Button';
import Loader from '../common/Loader';
import InlineLoader from '../common/InlineLoader';
import { fromBN, formatUSDT, toBN } from '../../utils/format';
import { parseError } from '../../utils/errors';

export default function RequestWithdraw({ onSuccess }) {
  const { account, wrongNetwork } = useWallet();
  const { main, mainW } = useContracts();
  const { notify } = useNotify();

  const [pending, setPending] = useState(ethers.BigNumber.from(0));
  const [minW, setMinW] = useState(10);
  const [mult, setMult] = useState(10);
  const [claimDed, setClaimDed] = useState(1000);
  const [weekday, setWeekday] = useState(true);
  const [pendingReqCount, setPendingReqCount] = useState(0);
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(true);
  const [txStep, setTxStep] = useState(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    if (!account) { setLoading(false); return; }
    const load = async () => {
      try {
        const uid = await main.addrToId(account);
        if (uid.eq(0)) { if (mountedRef.current) setLoading(false); return; }
        const [basic, _min, _mult, _ded, _wd, userWCount] = await Promise.all([
          main.getUserBasic(uid),
          main.minimumWithdraw(),
          main.withdrawMultiple(),
          main.claimDeduction(),
          main.isWeekdayNow().catch(() => true),
          main.getUserWithdrawIdsLength(uid).catch(() => ethers.BigNumber.from(0))
        ]);
        if (!mountedRef.current) return;
        setPending(basic[4]);
        setMinW(fromBN(_min));
        setMult(fromBN(_mult));
        setClaimDed(Number(_ded));
        setWeekday(_wd);

        let pendingCount = 0;
        const totalUserW = Number(userWCount.toString());
        const scanN = Math.min(totalUserW, 20);
        for (let i = 0; i < scanN; i++) {
          const idx = totalUserW - 1 - i;
          try {
            const wid = await main.getUserWithdrawIdAt(uid, idx);
            const req = await main.withdrawRequests(wid);
            if (Number(req.status) === 0) pendingCount++;
          } catch (_) {}
        }
        if (mountedRef.current) setPendingReqCount(pendingCount);
        setLoading(false);
      } catch (err) {
        if (mountedRef.current) setLoading(false);
      }
    };
    setLoading(true);
    load();
    return () => { mountedRef.current = false; };
  }, [account, main, txStep]);

  const amtNum = Number(amount) || 0;
  const dedPct = claimDed / 10000;
  const netAmount = amtNum > 0 ? amtNum - amtNum * dedPct : 0;

  const doRequest = async () => {
    if (!account) { notify('error', 'Connect wallet first'); return; }
    if (wrongNetwork) { notify('error', 'Switch network first'); return; }
    if (!weekday) { notify('error', 'Withdrawal requests only open on weekdays'); return; }
    if (amtNum < minW) { notify('error', `Minimum withdrawal is $${minW}`); return; }
    if (amtNum % mult !== 0) { notify('error', `Amount must be multiple of $${mult}`); return; }
    if (toBN(amtNum).gt(pending)) { notify('error', 'Amount exceeds available balance'); return; }

    try {
      setTxStep('Submitting withdrawal request');
      const tx = await mainW.requestWithdraw(toBN(amtNum));
      setTxStep('Waiting for confirmation');
      await tx.wait();
      notify('success', `Request for $${amount} submitted! Awaiting admin approval.`);
      setAmount('');
      setTxStep(null);
      if (onSuccess) onSuccess();
    } catch (err) {
      setTxStep(null);
      notify('error', parseError(err));
    }
  };

  const setMax = () => {
    const pendNum = fromBN(pending);
    const maxAmt = Math.floor(pendNum / mult) * mult;
    setAmount(maxAmt.toString());
  };

  if (loading) return <Card title="Request Withdrawal"><InlineLoader text="Loading..." /></Card>;

  return (
    <>
      {txStep && <Loader text={txStep} />}
      <Card title="Request Withdrawal" glow className="form-card">
        <div className="grid grid-2 mb-4">
          <div>
            <div className="text-muted" style={{ fontSize: 11, letterSpacing: 1.5 }}>AVAILABLE</div>
            <div className="mono text-success" style={{ fontSize: 22, fontWeight: 700 }}>${formatUSDT(pending)}</div>
          </div>
          <div>
            <div className="text-muted" style={{ fontSize: 11, letterSpacing: 1.5 }}>STATUS</div>
            <div className={weekday ? 'text-success mono' : 'text-danger mono'} style={{ fontSize: 14, fontWeight: 700, letterSpacing: 1.5 }}>
              {weekday ? 'MARKET OPEN' : 'CLOSED (WEEKEND)'}
            </div>
          </div>
        </div>

        {pendingReqCount > 0 && (
          <div className="form-summary mb-3" style={{ borderColor: 'var(--warning)' }}>
            <span className="text-warning" style={{ letterSpacing: 1 }}>
              ⏳ You have {pendingReqCount} pending request{pendingReqCount > 1 ? 's' : ''} awaiting approval
            </span>
          </div>
        )}

        <div className="input-group">
          <label className="input-label">Withdrawal Amount (USDT)</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              className="input"
              type="number"
              min={minW}
              step={mult}
              placeholder={`Min $${minW}, multiples of $${mult}`}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
            <Button variant="ghost" size="sm" onClick={setMax}>MAX</Button>
          </div>
          <div className="input-hint">Min: ${minW} · Step: ${mult} · Fee: {(dedPct * 100).toFixed(1)}% on approval</div>
        </div>

        {amtNum > 0 && (
          <div className="form-summary">
            <div><span className="sum-key">Requested:</span> <span className="sum-val">${amtNum.toFixed(2)}</span></div>
            <div><span className="sum-key">Fee ({(dedPct * 100).toFixed(1)}%):</span> <span className="text-danger">-${(amtNum * dedPct).toFixed(2)}</span></div>
            <div><span className="sum-key">You Receive:</span> <span className="text-success">${netAmount.toFixed(2)}</span></div>
            <div className="mt-2 text-warning" style={{ fontSize: 11 }}>
              ⚠ Amount will be locked until admin approves
            </div>
          </div>
        )}

        <div className="mt-6">
          <Button
            block
            variant="success"
            size="lg"
            onClick={doRequest}
            disabled={!!txStep || !weekday || !account || wrongNetwork || pending.eq(0)}
          >
            {!weekday ? 'MARKET CLOSED' : 'REQUEST WITHDRAWAL'}
          </Button>
        </div>
      </Card>
    </>
  );
}
